// Permission constants for the application
// These should match the permissions defined in the backend

export const PERMISSIONS = {
  // Product permissions
  PRODUCT: {
    VIEW: 'manage_product_view',
    CREATE: 'manage_product_create',
    EDIT: 'manage_product_edit',
    DELETE: 'manage_product_delete',
    SHOW: 'manage_product_show',
    IMPORT: 'manage_product_import',
    EXPORT: 'manage_product_export',
  },

  // Product review moderation permissions
  PRODUCT_REVIEW: {
    VIEW: 'manage_product_review_view',
    EDIT: 'manage_product_review_edit',
  },

  // Category permissions
  CATEGORY: {
    VIEW: 'manage_category_view',
    CREATE: 'manage_category_create',
    EDIT: 'manage_category_edit',
    DELETE: 'manage_category_delete',
    SHOW: 'manage_category_show',
    IMPORT: 'manage_category_import',
    EXPORT: 'manage_category_export',
  },

  // Section permissions
  SECTION: {
    VIEW: 'manage_section_view',
    CREATE: 'manage_section_create',
    EDIT: 'manage_section_edit',
    DELETE: 'manage_section_delete',
    SHOW: 'manage_section_show',
  },

  // Home Static Content permissions
  HOME_STATIC_CONTENT: {
    VIEW: 'manage_home_static_content_view',
    CREATE: 'manage_home_static_content_create',
    EDIT: 'manage_home_static_content_edit',
    DELETE: 'manage_home_static_content_delete',
    SHOW: 'manage_home_static_content_show',
  },

  // Brand permissions
  BRAND: {
    VIEW: 'manage_brand_view',
    CREATE: 'manage_brand_create',
    EDIT: 'manage_brand_edit',
    DELETE: 'manage_brand_delete',
    SHOW: 'manage_brand_show',
    IMPORT: 'manage_brand_import',
    EXPORT: 'manage_brand_export',
  },

  // Manufacturer permissions
  MANUFACTURER: {
    VIEW: 'manage_manufacturer_view',
    CREATE: 'manage_manufacturer_create',
    EDIT: 'manage_manufacturer_edit',
    DELETE: 'manage_manufacturer_delete',
    SHOW: 'manage_manufacturer_show',
    IMPORT: 'manage_manufacturer_import',
  },

  // Extra (catalog definition) permissions — independent of manage_product_*
  EXTRA: {
    VIEW: 'manage_extra_view',
    CREATE: 'manage_extra_create',
    EDIT: 'manage_extra_edit',
    DELETE: 'manage_extra_delete',
    SHOW: 'manage_extra_show',
    IMPORT: 'manage_extra_import',
  },

  // Product attribute (catalog definition) permissions — independent of manage_product_*
  PRODUCT_ATTRIBUTE: {
    VIEW: 'manage_product_attribute_view',
    CREATE: 'manage_product_attribute_create',
    EDIT: 'manage_product_attribute_edit',
    DELETE: 'manage_product_attribute_delete',
    SHOW: 'manage_product_attribute_show',
    IMPORT: 'manage_product_attribute_import',
  },

  // Keyword (catalog definition) permissions — independent of manage_product_*
  KEYWORD: {
    VIEW: 'manage_keyword_view',
    CREATE: 'manage_keyword_create',
    EDIT: 'manage_keyword_edit',
    DELETE: 'manage_keyword_delete',
    SHOW: 'manage_keyword_show',
    IMPORT: 'manage_keyword_import',
  },

  // Slider permissions
  SLIDER: {
    VIEW: 'manage_slider_view',
    CREATE: 'manage_slider_create',
    EDIT: 'manage_slider_edit',
    DELETE: 'manage_slider_delete',
    SHOW: 'manage_slider_show',
  },

  // Cancel Reason permissions
  CANCEL_REASON: {
    VIEW: 'manage_cancel_reason_view',
    CREATE: 'manage_cancel_reason_create',
    EDIT: 'manage_cancel_reason_edit',
    DELETE: 'manage_cancel_reason_delete',
    SHOW: 'manage_cancel_reason_show',
  },

  // Uncompleted Order Reason — backend seeds a single module permission
  // (granted to roles that already have cancel_reason). Action-suffixed
  // titles are also accepted where we OR-check for forward compatibility.
  UNCOMPLETED_ORDER_REASON: {
    VIEW: 'uncompleted_order_reason',
    CREATE: 'uncompleted_order_reason',
    EDIT: 'uncompleted_order_reason',
    DELETE: 'uncompleted_order_reason',
    SHOW: 'uncompleted_order_reason',
  },

  // Prescription review (CN-2615)
  PRESCRIPTION_STATUS: {
    VIEW: 'manage_prescription_status_view',
    CREATE: 'manage_prescription_status_create',
    EDIT: 'manage_prescription_status_edit',
    DELETE: 'manage_prescription_status_delete',
    SHOW: 'manage_prescription_status_show',
  },
  PRESCRIPTION_REJECT_REASON: {
    VIEW: 'manage_prescription_reject_reason_view',
    CREATE: 'manage_prescription_reject_reason_create',
    EDIT: 'manage_prescription_reject_reason_edit',
    DELETE: 'manage_prescription_reject_reason_delete',
    SHOW: 'manage_prescription_reject_reason_show',
  },
  PRESCRIPTION_REVIEW: {
    VIEW: 'manage_prescription_review_view',
    SHOW: 'manage_prescription_review_show',
    EDIT: 'manage_prescription_review_edit',
    REVIEW: 'review_prescription',
  },

  // Refund workflow (CN-2636). CREATE = maker, APPROVE = checker, PROCESS = settle.
  REFUND: {
    VIEW: 'manage_refund_view',
    SHOW: 'manage_refund_show',
    CREATE: 'manage_refund_create',
    APPROVE: 'manage_refund_approve',
    PROCESS: 'manage_refund_process',
  },
  REFUND_APPROVAL_RULE: {
    VIEW: 'manage_refund_approval_rule_view',
    SHOW: 'manage_refund_approval_rule_show',
    CREATE: 'manage_refund_approval_rule_create',
    EDIT: 'manage_refund_approval_rule_edit',
    DELETE: 'manage_refund_approval_rule_delete',
  },

  // Skill permissions
  SKILL: {
    VIEW: 'manage_skill_view',
    CREATE: 'manage_skill_create',
    EDIT: 'manage_skill_edit',
    DELETE: 'manage_skill_delete',
    SHOW: 'manage_skill_show',
  },

  // Service Type permissions
  SERVICE_TYPE: {
    VIEW: 'manage_service_type_view',
    CREATE: 'manage_service_type_create',
    EDIT: 'manage_service_type_edit',
    DELETE: 'manage_service_type_delete',
    SHOW: 'manage_service_type_show',
  },

  // Trip Type permissions
  TRIP_TYPE: {
    VIEW: 'manage_trip_type_view',
    CREATE: 'manage_trip_type_create',
    EDIT: 'manage_trip_type_edit',
    DELETE: 'manage_trip_type_delete',
    SHOW: 'manage_trip_type_show',
  },

  // Shopper permissions
  SHOPPER: {
    // Keep a single canonical shopper permission for visibility checks.
    // VIEW is aliased for backward compatibility in existing callers.
    VIEW: 'shopper_show',
    CREATE: 'shopper_create',
    EDIT: 'shopper_edit',
    DELETE: 'shopper_delete',
    SHOW: 'shopper_show',
    REPORT: 'shopper_report',
  },

  // Customer permissions (distinct from USER — agents/staff use USER.*)
  // POS order flow does not require these; ORDER.CREATE gates that page.
  CUSTOMER: {
    VIEW: 'customer_view',
    CREATE: 'customer_create',
    EDIT: 'customer_edit',
    DELETE: 'customer_delete',
    SHOW: 'customer_view',
    EXPORT: 'customer_export',
    IMPORT: 'customer_import',
    MANAGE: 'customer_view',
  },

  // Order permissions
  ORDER: {
    VIEW: 'order_view',
    CREATE: 'order_create',
    EDIT: 'order_edit',
    DELETE: 'order_delete',
    SHOW: 'order_show',
    VIEW_TRACKING: 'order_view_tracking',
    MAP_TRACKING: 'order_map_tracking',
    PRINT_A4: 'order_print_a4',
    CHANGE_STATUS: 'order_change_status',
    FORCE_CHANGE_STATUS: 'force_change_order_status',
    ADD_PRODUCT: 'order_add_product',
    EDIT_PRODUCT: 'order_edit_product',
    CHANGE_ADDRESS: 'order_change_address',
    CHANGE_DELIVERY_TIME: 'order_change_delivery_time',
    CHANGE_PAYMENT_METHOD: 'can_change_payment_method',
    EXPORT: 'order_export',
    TRANSFER: 'transfer_order',
    UPDATE: 'update_order',
    ADD_DISCOUNT: 'add_discount_order',
    UPDATE_SEGMENTATION: 'can_change_segmentation',
    ATTACHMENT: 'order_attachment',
  },

  // Odoo permissions (only exist once Awfar assigns the odoo model to the company)
  ODOO: {
    SHOW: 'odoo_show',
    NOT_DELIVERED_REFUND: 'odoo_not_delivered_refund',
    RETURN_HISTORY: 'odoo_return_history',
  },

  // Salla Orders permissions
  SALLA_ORDERS: {
    VIEW: 'salla_orders',
    ALL: 'manage_salla_order_all',
    PURCHASED_VIEW: 'manage_salla_order_purchased',
    PURCHASE: 'purchase_salla_orders',
    PURCHASED: 'purchased_salla_orders',
  },

  // Supplier permissions
  SUPPLIER: {
    VIEW: 'manage_supplier_view',
    CREATE: 'manage_supplier_create',
    EDIT: 'manage_supplier_edit',
    DELETE: 'manage_supplier_delete',
    SHOW: 'manage_supplier_view',
  },

  // Company permissions
  COMPANY: {
    VIEW: 'store_view', // API returns store_view
    CREATE: 'store_create',
    EDIT: 'store_edit',
    DELETE: 'store_delete',
    SHOW: 'store_show',
    SETTINGS: 'company_settings', // API returns company_settings
  },

  // Static pages (e-commerce module; previously gated by company_settings)
  PAGES: {
    VIEW: 'manage_pages_view',
    EDIT: 'manage_pages_edit',
  },

  // Short links (e-commerce module; previously gated by company_settings)
  SHORT_LINK: {
    VIEW: 'manage_short_link_view',
    SHOW: 'manage_short_link_show',
    CREATE: 'manage_short_link_create',
    EDIT: 'manage_short_link_edit',
    DELETE: 'manage_short_link_delete',
  },

  // Payment Settings (previously gated by company_settings)
  PAYMENT_SETTINGS: {
    VIEW: 'manage_payment_settings_view',
    EDIT: 'manage_payment_settings_edit',
  },

  // Notification Sounds (previously gated by company_settings)
  NOTIFICATION_SOUNDS: {
    VIEW: 'manage_notification_sounds_view',
    EDIT: 'manage_notification_sounds_edit',
  },

  // API Keys (previously gated by company_settings)
  API_KEYS: {
    VIEW: 'manage_api_keys_view',
    EDIT: 'manage_api_keys_edit',
  },

  // Call Channel permissions
  CALL_CHANNEL: {
    VIEW: 'call_channel_view',
    CREATE: 'call_channel_create',
    EDIT: 'call_channel_edit',
    DELETE: 'call_channel_delete',
    SHOW: 'call_channel_show',
  },

  // Location permissions
  COUNTRY: {
    VIEW: 'manage_country_view',
    CREATE: 'manage_country_create',
    EDIT: 'manage_country_edit',
    DELETE: 'manage_country_delete',
    SHOW: 'manage_country_show',
  },

  CITY: {
    VIEW: 'manage_city_view',
    CREATE: 'manage_city_create',
    EDIT: 'manage_city_edit',
    DELETE: 'manage_city_delete',
    SHOW: 'manage_city_show',
  },

  AREA: {
    VIEW: 'manage_area_view',
    CREATE: 'manage_area_create',
    EDIT: 'manage_area_edit',
    DELETE: 'manage_area_delete',
    SHOW: 'manage_area_show',
  },

  REGION: {
    VIEW: 'manage_region_view',
    CREATE: 'manage_region_create',
    EDIT: 'manage_region_edit',
    DELETE: 'manage_region_delete',
    SHOW: 'manage_region_show',
  },

  // Order Status permissions
  ORDER_STATUS: {
    VIEW: 'order_status_view',
    CREATE: 'order_status_create',
    EDIT: 'order_status_edit',
    DELETE: 'order_status_delete',
    SHOW: 'order_status_show',
  },

  // Delivery Time permissions
  DELIVERY_TIME: {
    VIEW: 'delivery_time_view',
    CREATE: 'delivery_time_create',
    EDIT: 'delivery_time_edit',
    DELETE: 'delivery_time_delete',
    SHOW: 'delivery_time_show',
  },

  // Store Management permissions
  STORE_MANAGEMENT: {
    VIEW: 'store_view',
    CREATE: 'store_create',
    EDIT: 'store_edit',
    DELETE: 'store_delete',
    SHOW: 'store_show',
  },

  // Activity permissions
  ACTIVITY: {
    VIEW: 'activity_view', // API returns activity_view
    CREATE: 'activity_create',
    EDIT: 'activity_edit',
    DELETE: 'activity_delete',
    SHOW: 'activity_show',
    MANAGE: 'activity_track', // API returns activity_track
    EXPORT: 'activity_export',
    VIEW_CLOSED_TICKETS: 'activity_view_closed_tickets',
    NOTIFICATION: 'activity_notification',
  },

  // Activity Type permissions
  ACTIVITY_TYPE: {
    VIEW: 'activity_type_view',
    CREATE: 'activity_type_create',
    EDIT: 'activity_type_edit',
    DELETE: 'activity_type_delete',
    SHOW: 'activity_type_show',
  },

  // Activity Status permissions
  ACTIVITY_STATUS: {
    VIEW: 'activity_status_view',
    CREATE: 'activity_status_create',
    EDIT: 'activity_status_edit',
    DELETE: 'activity_status_delete',
    SHOW: 'activity_status_show',
  },
  // CN-2382: ticket priorities with closing time (SLA).
  ACTIVITY_PRIORITY: {
    VIEW: 'activity_priority_view',
    CREATE: 'activity_priority_create',
    EDIT: 'activity_priority_edit',
    DELETE: 'activity_priority_delete',
  },

  // Store permissions
  STORE: {
    VIEW: 'manage_store_view',
    CREATE: 'manage_store_create',
    EDIT: 'manage_store_edit',
    DELETE: 'manage_store_delete',
    SHOW: 'manage_store_show',
  },

  // User permissions
  USER: {
    VIEW: 'user_view',
    CREATE: 'user_create',
    EDIT: 'user_edit',
    DELETE: 'user_delete',
    SHOW: 'user_show',
  },

  // Custom column views (admin: configure another user's visible columns)
  CUSTOM_VIEWS: {
    MANAGE: 'manage_custom_views',
  },

  // File manager permissions
  FILE_MANAGER: {
    VIEW: 'file_manager_view',
    UPLOAD: 'file_manager_upload',
    DELETE: 'file_manager_delete',
    // Optional row, enforced server-side only (config
    // file_manager_upload.download_required_permissions), where it falls back to
    // the file-manager view permission. Deliberately not used to gate the UI —
    // see the note in Pages/Catalog/FileManager; listed here so the string has
    // one definition if that ever changes.
    DOWNLOAD: 'file_manager_download',
  },

  // Settings permissions
  SETTINGS: {
    VIEW: 'manage_slider_view', // API returns manage_slider_view for sliders
    EDIT: 'manage_slider_edit',
  },

  // Reports permissions
  REPORTS: {
    VIEW: 'report_view',
    DASHBOARD: 'report_dashboard',
    CONTROL: 'report_control',
    SHOPPER: 'shopper_report',
    EXPORT: 'manage_reports_export',
  },

  // Offer permissions
  OFFER: {
    VIEW: 'manage_offer_view',
    CREATE: 'manage_offer_create',
    EDIT: 'manage_offer_edit',
    DELETE: 'manage_offer_delete',
    SHOW: 'manage_offer_show',
    IMPORT: 'manage_offer_import',
    EXPORT: 'manage_offer_export',
  },

  // Flash Deal permissions
  FLASH_DEAL: {
    VIEW: 'manage_flash_deal_view',
    CREATE: 'manage_flash_deal_create',
    EDIT: 'manage_flash_deal_edit',
    DELETE: 'manage_flash_deal_delete',
    SHOW: 'manage_flash_deal_show',
  },

  // Promocode permissions
  PROMOCODE: {
    VIEW: 'manage_promo_code_view',
    CREATE: 'manage_promo_code_create',
    EDIT: 'manage_promo_code_edit',
    DELETE: 'manage_promo_code_delete',
    SHOW: 'manage_promo_code_show',
  },

  // SMS Integration permissions
  SMS: {
    VIEW: 'sms_integration_view',
    CREATE: 'sms_integration_create',
    EDIT: 'sms_integration_edit',
    DELETE: 'sms_integration_delete',
    SHOW: 'sms_integration_show',
    SEND: 'sms_integration_send',
  },

  // Payment Gateway Integration permissions
  PAYMENT_GATEWAY: {
    VIEW: 'payment_gateway_view',
    CREATE: 'payment_gateway_create',
    EDIT: 'payment_gateway_edit',
    DELETE: 'payment_gateway_delete',
    SHOW: 'payment_gateway_show',
    CONFIGURE: 'payment_gateway_configure',
  },

  // Company Configuration permissions (for integrations)
  COMPANY_CONFIGURATION: {
    VIEW: 'manage_company_configuration_view',
    SHOW: 'manage_company_configuration_show',
    CREATE: 'manage_company_configuration_create',
    EDIT: 'manage_company_configuration_edit',
  },

  // Customer Segmentation permissions
  CUSTOMER_SEGMENTATION: {
    VIEW: 'get_customer_segmentation',
    CREATE: 'create_customer_segmentation',
    UPDATE: 'update_customer_segmentation',
    DELETE: 'delete_customer_segmentation',
  },

  // SMS Template permissions
  SMS_TEMPLATE: {
    VIEW: 'manage_sms_template_view',
    EDIT: 'manage_sms_template_edit',
  },

  // Email Settings permissions
  EMAIL_SETTINGS: {
    VIEW: 'manage_email_settings_view',
    EDIT: 'manage_email_settings_edit',
    TEST: 'manage_email_settings_test',
  },

  // Push Notifications permissions
  PUSH_NOTIFICATIONS: {
    VIEW: 'manage_push_notification_view',
    CREATE: 'manage_push_notification_create',
    SEND: 'manage_push_notification_send',
    DELETE: 'manage_push_notification_delete',
    RESEND: 'manage_push_notification_resend',
  },

  // Organization permissions
  ORGANIZATION: {
    VIEW: 'manage_organization_view',
    CREATE: 'manage_organization_create',
    EDIT: 'manage_organization_edit',
    DELETE: 'manage_organization_delete',
    SHOW: 'manage_organization_show',
  },
// DMS permissions (only exist once Awfar assigns the dms model to the company)
DMS: {
  ORDER_DETAILS_SHOW: 'order_details_show_dms',
  DRIVER_TRACKER: {
    VIEW: 'dms_driver_tracker_view',
  },
  AGENT_KPI: {
    VIEW: 'dms_agent_kpi_view',
  },
  PREDICTION: {
    VIEW: 'dms_prediction_view',
  },
},

// Purchasing module permissions (Predict Products + Requests + All Requests)
// Mirrors the granularity of other DMS modules so the sidebar/route guards
// work the same way (`view` gates visibility, `manage` gates write actions).
PURCHASING: {
  PREDICT_PRODUCTS: {
    VIEW: 'manage_predict_products_view',
    MANAGE: 'manage_predict_products_manage',
  },
  REQUESTS: {
    VIEW: 'manage_purchasing_requests_view',
    MANAGE: 'manage_purchasing_requests_manage',
  },
  ALL_REQUESTS: {
    VIEW: 'manage_all_purchasing_requests_view',
    MANAGE: 'manage_all_purchasing_requests_manage',
  },
},

// Insurance Company permissions
INSURANCE: {
  VIEW: 'manage_insurance_view',
  CREATE: 'manage_insurance_create',
  EDIT: 'manage_insurance_edit',
  DELETE: 'manage_insurance_delete',
  SHOW: 'manage_insurance_view',
},

  // Role / permission / model management (matches old Blade @can checks)
  ROLE: {
    VIEW: 'role_view',
    CREATE: 'role_create',
    EDIT: 'role_edit',
    DELETE: 'role_delete',
  },
  PERMISSION: {
    VIEW: 'permission_view',
    CREATE: 'permission_create',
    EDIT: 'permission_edit',
    DELETE: 'permission_delete',
  },
  MODEL: {
    VIEW: 'model_view',
    CREATE: 'model_create',
    EDIT: 'model_edit',
    DELETE: 'model_delete',
  },

// Insurance Order (Insurance POS) permissions
INSURANCE_ORDER: {
  VIEW: 'manage_insurance_order_view',
  CREATE: 'manage_insurance_order_create',
  /** View prescriptions on insurance orders (gallery + attach type). */
  PRESCRIPTION_VIEW: 'prescription_view',
},

  // Multi-warehouse fulfilment (CN-2640)
  ORDER_FULFILMENT: {
    VIEW: 'manage_order_fulfilment_view',
    SHOW: 'manage_order_fulfilment_show',
    CREATE: 'manage_order_fulfilment_create',
    EDIT: 'manage_order_fulfilment_edit',
  },

} as const;

// Route permission mappings
export const ROUTE_PERMISSIONS = {
  // Catalog routes
  '/catalog/product': [PERMISSIONS.PRODUCT.VIEW],
  '/add-new-product': [PERMISSIONS.PRODUCT.CREATE],
  '/product/edit/:id': [PERMISSIONS.PRODUCT.EDIT],
  
  '/catalog/categories': [PERMISSIONS.CATEGORY.VIEW],
  '/add-new-category': [PERMISSIONS.CATEGORY.CREATE],
  '/edit-category': [PERMISSIONS.CATEGORY.EDIT],
  '/sub-categories': [PERMISSIONS.CATEGORY.VIEW],
  '/add-sub-category': [PERMISSIONS.CATEGORY.CREATE],
  
  '/catalog/brands': [PERMISSIONS.BRAND.VIEW],
  '/edit-brand': [PERMISSIONS.BRAND.EDIT],

  '/catalog/manufacturers': [PERMISSIONS.MANUFACTURER.VIEW],
  '/edit-manufacturer': [PERMISSIONS.MANUFACTURER.CREATE, PERMISSIONS.MANUFACTURER.EDIT],
  
  '/catalog/extra': [PERMISSIONS.EXTRA.VIEW],
  '/edit-extra': [PERMISSIONS.EXTRA.EDIT],

  '/catalog/product-attributes': [PERMISSIONS.PRODUCT_ATTRIBUTE.VIEW],
  '/catalog/keywords': [PERMISSIONS.KEYWORD.VIEW],
  '/catalog/product-reviews': [PERMISSIONS.PRODUCT_REVIEW.VIEW],
  '/catalog/review-banned-words': [PERMISSIONS.PRODUCT_REVIEW.VIEW],
  '/e-commerce/sections': [PERMISSIONS.SECTION.VIEW],

  // Order routes
  '/orders/list': [PERMISSIONS.ORDER.VIEW],
  '/orders/active': [PERMISSIONS.ORDER.VIEW],
  '/orders/show/:id': [PERMISSIONS.ORDER.SHOW],
  '/orders/track/:id': [PERMISSIONS.ORDER.VIEW_TRACKING],
  '/orders/tracking': [PERMISSIONS.ORDER.MAP_TRACKING],
  '/orders/fulfilment': [PERMISSIONS.ORDER_FULFILMENT.VIEW],
  '/orders/pharmacist-review': [
    PERMISSIONS.PRESCRIPTION_REVIEW.VIEW,
    PERMISSIONS.PRESCRIPTION_REVIEW.REVIEW,
  ],
  '/orders/pharmacist-review/:id': [
    PERMISSIONS.PRESCRIPTION_REVIEW.VIEW,
    PERMISSIONS.PRESCRIPTION_REVIEW.SHOW,
    PERMISSIONS.PRESCRIPTION_REVIEW.REVIEW,
  ],
  '/e-commerce/prescription-statuses': [PERMISSIONS.PRESCRIPTION_STATUS.VIEW],
  '/e-commerce/prescription-reject-reasons': [
    PERMISSIONS.PRESCRIPTION_REJECT_REASON.VIEW,
  ],
  '/orders/refunds': [PERMISSIONS.REFUND.VIEW],
  '/orders/refund-approval-rules': [PERMISSIONS.REFUND_APPROVAL_RULE.VIEW],

  // Company routes
  '/company-setting': [PERMISSIONS.COMPANY.SETTINGS],
  '/company-setting/store/deliver_location/:id': [PERMISSIONS.STORE_MANAGEMENT.CREATE],
  '/all-companies': [PERMISSIONS.COMPANY.VIEW],
  '/company-customizations': [PERMISSIONS.COMPANY.VIEW],
  '/company-customizations/:presetId': [PERMISSIONS.COMPANY.VIEW],
  '/edit-company': [PERMISSIONS.COMPANY.EDIT],
  '/create-company': [PERMISSIONS.COMPANY.CREATE],
  '/payment-settings': [PERMISSIONS.PAYMENT_SETTINGS.VIEW],
  '/notification-sounds': [PERMISSIONS.NOTIFICATION_SOUNDS.VIEW],

  // Activity routes
  '/all-activities': [PERMISSIONS.ACTIVITY.VIEW],
  // Types/Sections/Reasons use ACTIVITY_TYPE.VIEW; Status tab uses ACTIVITY_STATUS.VIEW
  '/activity-management': [
    PERMISSIONS.ACTIVITY_TYPE.VIEW,
    PERMISSIONS.ACTIVITY_STATUS.VIEW,
  ],

  // E-commerce routes
  '/e-commerce/sliders': [PERMISSIONS.SLIDER.VIEW],
  '/e-commerce/flash-deal': [PERMISSIONS.FLASH_DEAL.VIEW],
  '/e-commerce/promocodes': [PERMISSIONS.PROMOCODE.VIEW],
  '/e-commerce/home-static-content': [PERMISSIONS.HOME_STATIC_CONTENT.VIEW],

  // User routes
  '/users': [PERMISSIONS.USER.VIEW],
  '/users/customize-columns': [PERMISSIONS.CUSTOM_VIEWS.MANAGE],

  // Location routes
  '/locations/countries': [PERMISSIONS.COUNTRY.VIEW],
  '/locations/cities': [PERMISSIONS.CITY.VIEW],
  '/locations/areas': [PERMISSIONS.AREA.VIEW],
  '/locations/regions': [PERMISSIONS.REGION.VIEW],

  // Offer routes
  '/offers': [PERMISSIONS.OFFER.VIEW],
  '/offers/create': [PERMISSIONS.OFFER.CREATE],
  '/offers/edit/:id': [PERMISSIONS.OFFER.EDIT],
  '/offers-group': [PERMISSIONS.OFFER.VIEW],
  '/offers-group/create': [PERMISSIONS.OFFER.CREATE],
  '/offers-group/edit/:id': [PERMISSIONS.OFFER.EDIT],

  // Integration routes
  '/integrations/sms': [PERMISSIONS.COMPANY_CONFIGURATION.VIEW],
  '/integrations/payment-gateway': [PERMISSIONS.COMPANY_CONFIGURATION.VIEW],
  '/email-settings': [PERMISSIONS.EMAIL_SETTINGS.VIEW],
  '/settings/sms-templates': [PERMISSIONS.SMS_TEMPLATE.VIEW],
  '/customer-segmentation': [PERMISSIONS.CUSTOMER_SEGMENTATION.VIEW],
  '/company-settings/push-notifications': [PERMISSIONS.PUSH_NOTIFICATIONS.VIEW],

  // Pages / short links (e-commerce module)
  '/pages/about': [PERMISSIONS.PAGES.VIEW],
  '/pages/privacy': [PERMISSIONS.PAGES.VIEW],
  '/pages/terms': [PERMISSIONS.PAGES.VIEW],
  '/pages/faq': [PERMISSIONS.PAGES.VIEW],
  '/short-links': [PERMISSIONS.SHORT_LINK.VIEW],

  // File manager
  '/catalog/file-manager': [PERMISSIONS.FILE_MANAGER.VIEW],

  // Permissions / roles / models
  '/permissions': [
    PERMISSIONS.ROLE.VIEW,
    PERMISSIONS.PERMISSION.VIEW,
    PERMISSIONS.MODEL.VIEW,
  ],

  // DMS reports
  '/dms-reports/dashboard': [PERMISSIONS.DMS.DRIVER_TRACKER.VIEW],
  '/dms-reports/top-10-shoppers': [PERMISSIONS.DMS.DRIVER_TRACKER.VIEW],
  '/dms/driver-tracker': [PERMISSIONS.DMS.DRIVER_TRACKER.VIEW],
  '/dms/agent_kpi': [PERMISSIONS.DMS.AGENT_KPI.VIEW],
  '/dms/prediction': [PERMISSIONS.DMS.PREDICTION.VIEW],

} as const;

// Helper function to get permissions for a route
export const getRoutePermissions = (route: string): string[] => {
  // Try exact match first
  if (ROUTE_PERMISSIONS[route as keyof typeof ROUTE_PERMISSIONS]) {
    return [...ROUTE_PERMISSIONS[route as keyof typeof ROUTE_PERMISSIONS]];
  }

  // Try pattern matching for dynamic routes
  for (const [pattern, permissions] of Object.entries(ROUTE_PERMISSIONS)) {
    if (pattern.includes(':')) {
      const regexPattern = pattern.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${regexPattern}$`);
      if (regex.test(route)) {
        return [...permissions];
      }
    }
  }

  // Default to empty array if no permissions found
  return [];
};

// Helper function to check if a route requires specific permissions
export const routeRequiresPermissions = (route: string): boolean => {
  return getRoutePermissions(route).length > 0;
}; 